import List from '@pelckmans/elementis/components/list';
import Section from '@pelckmans/elementis/components/section';
import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import type { MyAssignment } from '../../../../../api/assignment';
import { ANALYTICS_EVENT_SUBLOCATIONS } from '../../../../../constants/analyticsEvents';
import MyAssignmentsContext from '../../../../../contexts/MyAssignmentsContext';
import { AssignmentDeadlinePill } from '../../../../../shared-components/AssignmentDeadlinePill';
import { MaterialIcon } from '../../../components/MaterialIcon';
import TableOfContentsContext from '../../_contexts/TableOfContentsContext';
import useAssignmentHandling from '../../hooks/assignments/useAssignmentHandling';
import ScorableProgress from '../ScorableProgress';
import PinnedMaterialButton from '../pinned-material/PinnedMaterialButton';

export default function AssignmentSection({ assignments: assignmentsByModule }: { assignments: MyAssignment[] }) {
  const { t } = useTranslation();
  const { refreshAssignments } = useContext(MyAssignmentsContext);
  const { selectedNode } = useContext(TableOfContentsContext);

  const { startAssignment, resumeAssignment } = useAssignmentHandling({
    onAssignmentClosed: refreshAssignments,
    subLocationId: ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_MODULE_ASSIGNMENTS,
  });

  const handleOpenAssignment = useCallback(
    async (assignment: MyAssignment) => {
      if (!selectedNode) return;

      if (Boolean(assignment.score.achievedAt)) {
        await resumeAssignment(assignment);
      } else {
        await startAssignment(assignment);
      }
    },
    [selectedNode, resumeAssignment, startAssignment],
  );

  if (assignmentsByModule.length === 0) return null;

  return (
    <Section title={t(`PAGES.TOC_LEVEL.SECTIONS.STUDENT.ASSIGNMENTS.TITLE`)}>
      {assignmentsByModule.length > 0 && (
        <List>
          {assignmentsByModule.map(assignment => (
            <List.Item
              onClick={() => handleOpenAssignment(assignment)}
              key={assignment.id}
              title={assignment.name}
              icon={<MaterialIcon materialType={assignment.materialType} contentType={assignment.contentType} />}
              pillGroupProps={{
                'aria-label': t('SHARED_COMPONENTS.ASSIGNMENT_DEADLINE_PILL.A11Y_LABEL'),
                role: 'group',
                children: <AssignmentDeadlinePill deadline={assignment.to} />,
              }}
              actionArea={
                <>
                  <ScorableProgress unachievable={assignment.unachievable} score={assignment.score} />
                  <PinnedMaterialButton type={assignment.materialType} materialId={assignment.materialId} />
                </>
              }
              as="button"
            />
          ))}
        </List>
      )}
    </Section>
  );
}
